import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DownloadIcon from "@mui/icons-material/Download";

const getFileType = (attachment) => {
  const extension = attachment.fileExtension?.toLowerCase() || '';
  const fileName = attachment.fileName?.toLowerCase() || '';
  const url = attachment.url?.toLowerCase() || '';

  if (extension.includes('image/') || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(fileName)) {
    return 'image';
  }
  
  // Check for PDFs (includes converted Word docs files)
  // Also check if URL contains Filestack PDF transformation
  if (extension.includes('pdf') || 
      fileName.endsWith('.pdf') || 
      url.includes('output=format:pdf') || 
      url.includes('output=f:pdf')) {
    return 'pdf';
  }

  // Everything else is unsupported for viewing
  return 'unsupported';
};

export default function AttachmentViewerModal({ open, onClose, attachments, initialIndex = 0 }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, currentIndex]);

  if (!attachments || attachments.length === 0) return null;

  const currentAttachment = attachments[currentIndex];
  const fileType = getFileType(currentAttachment);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < attachments.length - 1;

  const handlePrevious = () => {
    if (hasPrevious) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleDownload = () => {
    window.open(currentAttachment.url, '_blank');
  };

  const renderViewer = () => {
    switch (fileType) {
      case 'image':
        return (
          <Box
            component="img"
            src={currentAttachment.url}
            alt={currentAttachment.fileName}
            sx={{
              maxWidth: '100%',
              maxHeight: '70vh',
              objectFit: 'contain',
              margin: '0 auto',
              display: 'block',
            }}
          />
        );

      case 'pdf':
        return (
          <iframe
            src={currentAttachment.url}
            title={currentAttachment.fileName}
            style={{
              width: '100%',
              height: '70vh',
              border: 'none',
            }}
          />
        );

      case 'unsupported':
      default:
        return (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="300px"
            gap={2}
          >
            <Typography variant="body1" color="text.secondary">
              Preview not available for this file type
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Download File
            </Button>
          </Box>
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        borderBottom={1}
        borderColor="divider"
      >
        <IconButton onClick={onClose} size="small" sx={{ visibility: 'hidden' }}>
          <CloseIcon />
        </IconButton>
        <Box display="flex" alignItems="center" gap={1} flex={1} justifyContent="center">
          <Typography variant="h6" component="div" noWrap sx={{ maxWidth: '500px', color: 'black', fontWeight: 500 }}>
            {currentAttachment.fileName}
          </Typography>
          {attachments.length > 1 && (
            <Typography variant="body2" color="text.secondary">
              (Attachment {currentIndex + 1} of {attachments.length})
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={1}>
          <IconButton
            onClick={handleDownload}
            size="small"
            title="Download"
          >
            <DownloadIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ p: 0, position: 'relative' }}>
        <Box display="flex" alignItems="center" minHeight="70vh">
          {attachments.length > 1 && (
            <IconButton
              onClick={handlePrevious}
              disabled={!hasPrevious}
              sx={{
                position: 'absolute',
                left: 16,
                zIndex: 1,
                color: '#333',
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          <Box flex={1} p={2}>
            {renderViewer()}
          </Box>

          {attachments.length > 1 && (
            <IconButton
              onClick={handleNext}
              disabled={!hasNext}
              sx={{
                position: 'absolute',
                right: 16,
                zIndex: 1,
                color: '#333',
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ArrowForwardIcon />
            </IconButton>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
